import React, { useRef, useMemo, useEffect } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import * as yup from "yup";
import { MCheckboxGroup } from "components/Shared";
import MCard from "components/Shared/MCard.tsx";
import {
  DATA_TYPE_DROPDOWN2,
  DATA_TYPE_ADDRESS_AUTOCOMPLETE,
  DATA_TYPE_ADDRESS,
  DATA_TYPE_NUMBER_STRING,
  DATA_TYPE_STRING,
} from "typeConstants/dataTypes";
import OrderStepButtons from "components/WebOrderV2/OrderStepButtons";
import { LENDER } from "typeConstants/webOrder";
import { getZipCodes } from "actions/assignment";

const SingleProperty = (props) => {
  const mCardControlMethodsAddress = useRef({});
  const checkboxControlMethods = useRef({});

  useEffect(() => {
    props.getZipCodes();
  }, []);

  const setControlMethodsAddress = () => {
    return (cm) => (mCardControlMethodsAddress.current = cm);
  };

  const onSurveyAddressSearch = (address) => {
    mCardControlMethodsAddress.current.setEditData("surveyAddress1", address.streetAddress);
    mCardControlMethodsAddress.current.setEditData("surveyAddress2", "");
    mCardControlMethodsAddress.current.setEditData("surveyCity", address.city);
    mCardControlMethodsAddress.current.setEditData("surveyState", { label: address.state, value: address.state });
    mCardControlMethodsAddress.current.setEditData("surveyZipCode", address.zip);
  };

  const getAddressFields = () => {
    return [
      {
        key: "searchSurveyAddress",
        dataType: DATA_TYPE_ADDRESS_AUTOCOMPLETE,
        label: "Address Search",
        onChange: onSurveyAddressSearch,
        value: "",
      },
      {
        key: "surveyAddress1",
        dataType: DATA_TYPE_ADDRESS,
        label: "Address",
        value: props.data.surveyAddress1,
        validation: yup.string().max(75, "Address cannot exceed 75 characters").required("Address is required"),
        placeholder: "Required",
      },
      {
        key: "surveyAddress2",
        dataType: DATA_TYPE_STRING,
        label: "Address 2",
        value: props.data.surveyAddress2,
        validation: yup.string().max(75, "Address 2 cannot exceed 75 characters"),
      },
      {
        key: "surveyCity",
        label: "City",
        value: props.data.surveyCity,
        validation: yup.string().max(50, "City cannot exceed 50 characters").required("City is required"),
        placeholder: "Required",
      },
      {
        key: "surveyState",
        dataType: DATA_TYPE_DROPDOWN2,
        label: "State",
        value: props.data.surveyState,
        dataTypeOptions: { options: props.stateOptions },
        validation: yup.string().required("State is required"),
        placeholder: "Select... (required)",
      },
      {
        key: "surveyZipCode",
        dataType: DATA_TYPE_NUMBER_STRING,
        label: "Zip",
        value: props.data.surveyZipCode,
        validation: yup
          .string()
          .min(5, "Value must be 5 digits")
          .max(5, "Value must be 5 digits")
          .test(
            "is-valid-zip",
            (z) => `${z.value} is not a valid zip code for the entered state`,
            (z) => {
              //When going back to the previous page, the mCardControlMethodsAddress is wiped. If that's the case, validate against the currently stored order data instead
              let editedState = mCardControlMethodsAddress.current.getEditedDataObject().surveyState;
              // Handle both object and string formats for state value
              let stateValue = editedState?.value ?? editedState ?? props.data.surveyState?.value ?? props.data.surveyState;
              return props.zipCodes.find((zip) => z === zip.zipCode && stateValue === zip.stateAbbreviation);
            },
          )
          .required("Zip is required"),
        placeholder: "Required",
        onChange: (value) => props.onPostalCodeChange(value),
      },
    ];
  };

  const memoizedSupplementCheckboxes = useMemo(() => {
    return props.availableSupplements.map((s) => {
      let isSelected = props.data.supplements?.includes(s.code);
      return {
        label: s.name,
        value: s.code,
        defaultChecked: isSelected,
      };
    });
  }, [props.availableSupplements]);

  const onNext = () => {
    let isAddressValid = mCardControlMethodsAddress.current.runValidation(true);

    if (isAddressValid) {
      let addressData = mCardControlMethodsAddress.current.getEditedDataObject();
      let editedData = { ...addressData };

      let supplements = props.availableSupplements.length > 0 ? checkboxControlMethods.current?.getChecked() : [];
      editedData.supplements = supplements;

      if (editedData.surveyState) {
        editedData.surveyState = props.stateOptions.filter((s) => s.value === editedData.surveyState)[0];
      }
      props.onNext(editedData);
    }
  };
  const onPrevious = () => {
    let addressData = mCardControlMethodsAddress.current.getEditedDataObject();
    let editedData = { ...addressData };
    props.onPrevious(editedData);
  };

  let supplementsLabel = props.terminology === LENDER ? "Additional Forms" : "Supplements";

  return (
    <>
      <MCard
        title="Property Address"
        getControlMethods={setControlMethodsAddress("address")}
        fields={getAddressFields()}
        onlyEditable
      />
      <MCard title={supplementsLabel}>
        {props.availableSupplements.length > 0 ? (
          <MCheckboxGroup
            controlMethods={(cm) => (checkboxControlMethods.current = cm)}
            fields={memoizedSupplementCheckboxes}
          />
        ) : (
          <p>{`No ${supplementsLabel} Allowed`}</p>
        )}
      </MCard>
      <OrderStepButtons currentStep={props.currentStep} onPrevious={onPrevious} onNext={onNext} />
    </>
  );
};

SingleProperty.propTypes = {
  currentStep: PropTypes.number, // Current step in order process, needed for OrderStepButtons
  data: PropTypes.object, // Data for MCard fields
  onNext: PropTypes.func, // Func to call when fields are valid to move to the next step
  onPrevious: PropTypes.func, // Func to call to move to previous step
  terminology: PropTypes.string, // Insurance or Lender, see typeConstants/webOrder.js
  pageLayout: PropTypes.string, // LenderAppraisal, LenderNonAppraisal, Insurance, Commercial: see typeConstants/webOrder.js
  handlingFeatures: PropTypes.object, // caseHandlingFeatures from settings
  stateOptions: PropTypes.array, // State abbrevation options for address field
  availableSupplements: PropTypes.array,
  onPostalCodeChange: PropTypes.func,
  zipCodes: PropTypes.array,
};

const mapStateToProps = (state) => {
  return {
    zipCodes: state.assignment.zipCodes,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    getZipCodes(stateCodeList) {
      dispatch(getZipCodes(stateCodeList));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(SingleProperty);
